/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// EVENT COALESCER - Debounce/Throttle High-Volume Events
// ============================================================================

import { logger } from "@/lib/utils/logger";
import { realtimeMetrics } from "./realtime-metrics";

// ============================================================================
// TYPES
// ============================================================================

export interface CoalescedEvent<T = unknown> {
  events: Array<{ payload: T; timestamp: number }>;
  organizationId: string;
  table: string;
  eventType: "INSERT" | "UPDATE" | "DELETE" | "UNKNOWN";
  coalescedAt: number;
}

export interface EventCoalescerOptions {
  debounceMs?: number; // Wait for quiet period before emitting
  throttleMs?: number; // Maximum emit rate
  maxBatchSize?: number; // Maximum events per batch
  organizationId: string;
  table: string;
}

export type EventHandler<T = unknown> = (
  event: CoalescedEvent<T>,
) => void | Promise<void>;

// ============================================================================
// EVENT COALESCER
// ============================================================================

export class EventCoalescer<T = unknown> {
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private throttleTimer: ReturnType<typeof setTimeout> | null = null;
  private eventBuffer: Array<{
    payload: T;
    timestamp: number;
    eventType: "INSERT" | "UPDATE" | "DELETE" | "UNKNOWN";
  }> = [];
  private lastEmitTime = 0;
  private readonly debounceMs: number;
  private readonly throttleMs: number;
  private readonly maxBatchSize: number;
  private readonly organizationId: string;
  private readonly table: string;
  private readonly handler: EventHandler<T>;
  private isProcessing = false;

  constructor(options: EventCoalescerOptions & { handler: EventHandler<T> }) {
    this.debounceMs = options.debounceMs ?? 100; // Default 100ms debounce
    this.throttleMs = options.throttleMs ?? 1000; // Default 1s throttle
    this.maxBatchSize = options.maxBatchSize ?? 50; // Default 50 events per batch
    this.organizationId = options.organizationId;
    this.table = options.table;
    this.handler = options.handler;
  }

  // ============================================================================
  // EVENT PROCESSING
  // ============================================================================

  add(
    payload: T,
    eventType: "INSERT" | "UPDATE" | "DELETE" | "UNKNOWN" = "UNKNOWN",
  ): void {
    const timestamp = Date.now();

    // Record event in metrics
    realtimeMetrics.recordEvent(
      this.organizationId,
      this.table,
      eventType,
      false,
      false,
    );

    // Add to buffer
    this.eventBuffer.push({ payload, timestamp, eventType });

    // Check if we should emit immediately (throttle)
    const timeSinceLastEmit = timestamp - this.lastEmitTime;
    if (timeSinceLastEmit >= this.throttleMs) {
      this.emitImmediate();
      return;
    }

    // Check if buffer is full
    if (this.eventBuffer.length >= this.maxBatchSize) {
      this.emitImmediate();
      return;
    }

    // Schedule debounced emit
    this.scheduleDebouncedEmit();
  }

  // ============================================================================
  // EMIT LOGIC
  // ============================================================================

  private emitImmediate(): void {
    if (this.isProcessing || this.eventBuffer.length === 0) {
      return;
    }

    this.isProcessing = true;

    // Clear timers
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
      this.throttleTimer = null;
    }

    // Extract events
    const events = this.eventBuffer.splice(0, this.maxBatchSize);
    this.lastEmitTime = Date.now();

    // Determine event type (most common in batch)
    const eventTypeCounts = {
      INSERT: 0,
      UPDATE: 0,
      DELETE: 0,
      UNKNOWN: 0,
    };
    for (const event of events) {
      eventTypeCounts[event.eventType]++;
    }
    const sortedEntries = (
      Object.entries(eventTypeCounts) as Array<
        [keyof typeof eventTypeCounts, number]
      >
    ).sort((a, b) => b[1] - a[1]);
    const mostCommonType = sortedEntries[0]?.[0] ?? "UNKNOWN";

    // Create coalesced event
    const coalescedEvent: CoalescedEvent<T> = {
      events: events.map((e) => ({
        payload: e.payload,
        timestamp: e.timestamp,
      })),
      organizationId: this.organizationId,
      table: this.table,
      eventType: mostCommonType,
      coalescedAt: this.lastEmitTime,
    };

    // Emit
    try {
      const result = this.handler(coalescedEvent);
      if (result instanceof Promise) {
        result
          .catch((error) => {
            logger.error(
              `[EVENT COALESCER] Handler error for ${this.table}:`,
              error,
            );
            realtimeMetrics.recordError(this.organizationId, this.table);
          })
          .finally(() => {
            this.isProcessing = false;
          });
      } else {
        this.isProcessing = false;
      }
    } catch (error) {
      logger.error(`[EVENT COALESCER] Handler error for ${this.table}:`, error);
      realtimeMetrics.recordError(this.organizationId, this.table);
      this.isProcessing = false;
    }
  }

  private scheduleDebouncedEmit(): void {
    if (this.debounceTimer) {
      return; // Already scheduled
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.emitImmediate();
    }, this.debounceMs);
  }

  // ============================================================================
  // FLUSH
  // ============================================================================

  flush(): void {
    if (this.eventBuffer.length > 0) {
      this.emitImmediate();
    }
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  destroy(): void {
    this.flush();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
      this.throttleTimer = null;
    }

    this.eventBuffer = [];
  }
}

// ============================================================================
// COALESCER FACTORY
// ============================================================================

export function createEventCoalescer<T = unknown>(
  options: EventCoalescerOptions & { handler: EventHandler<T> },
): EventCoalescer<T> {
  return new EventCoalescer(options);
}
